/**
 * SectionIcons — Step header icons + SectionIcon title component.
 * Used above each wizard step card.
 */

import type { ReactNode } from 'react';
import { T } from '@/styles/tokens';

const ICONS: Record<string, ReactNode> = {
  artwork: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="1.5" y="1.5" width="15" height="15" rx="3" stroke={T.primary} strokeWidth="1.6" />
      <path d="M1.5 12.5l4.5-4.5 3 3 4-5 3.5 3.5" stroke={T.primary} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="5.5" r="1.5" fill={T.primary} opacity="0.4" />
    </svg>
  ),
  clock: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke={T.primary} strokeWidth="1.6" />
      <path d="M9 5v4.5l3 1.5" stroke={T.primary} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  materials: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M4 14V4h10v10H4z" stroke={T.primary} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M7 8h4M7 11h2" stroke={T.primary} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  detail: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2 14l4.5-9 3.5 4.5 2-3 4 7.5" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="6.5" cy="5" r="1.5" stroke={T.primary} strokeWidth="1.2" fill="none" />
    </svg>
  ),
  price: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke={T.primary} strokeWidth="1.6" />
      <path d="M9 5v8M7 6.5h3.5a1.5 1.5 0 010 3H7.5h3a1.5 1.5 0 010 3H7" stroke={T.primary} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  surface: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3 15h12" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 12l3-9h6l3 9" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  channel: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M15.2 10.2l-5.4 5.4a1.5 1.5 0 01-2.1 0L1.5 9.5V1.5h8l5.7 5.7a1.5 1.5 0 010 2.1v0z" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="5.5" cy="5.5" r="1" fill={T.primary} opacity="0.5" />
    </svg>
  ),
};

interface SectionIconProps {
  icon: string;
  label: string;
}

export function SectionIcon({ icon, label }: SectionIconProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
      padding: '0 4px',
    }}>
      <div style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: T.tealSoft,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {ICONS[icon]}
      </div>
      <span style={{
        fontSize: 13,
        fontWeight: 700,
        color: T.text,
        fontFamily: T.fontFamily,
        letterSpacing: '-0.01em',
      }}>
        {label}
      </span>
    </div>
  );
}

export { ICONS as SECTION_ICONS };
